import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Home, Loader2, BookOpen, Eye, Download,
  Search, X, Bookmark, BookmarkCheck, Trash2, Share2, Twitter, Copy, Check, Facebook
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LANDING_URL = window.location.origin;

// --- localStorage helpers with validation ---
const STORAGE_KEYS = {
  progress: (bookNum) => `hdominion_progress_${bookNum}`,
  bookmarks: (bookNum) => `hdominion_bookmarks_${bookNum}`,
};

function loadProgress(bookNumber) {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.progress(bookNumber));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.chapterIndex !== "number" || parsed.chapterIndex < 0) return null;
    return parsed;
  } catch (e) {
    // Failed to load reading progress
    return null;
  }
}

function saveProgress(bookNumber, chapterIndex) {
  try {
    localStorage.setItem(
      STORAGE_KEYS.progress(bookNumber),
      JSON.stringify({ chapterIndex, updatedAt: Date.now() })
    );
  } catch (e) {
    // Failed to save reading progress
  }
}

function loadBookmarks(bookNumber) {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.bookmarks(bookNumber));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(b => b && typeof b.id === "string" && typeof b.text === "string");
  } catch (e) {
    // Failed to load bookmarks
    return [];
  }
}

function persistBookmarks(bookNumber, bookmarks) {
  try {
    localStorage.setItem(STORAGE_KEYS.bookmarks(bookNumber), JSON.stringify(bookmarks));
  } catch (e) {
    // Failed to save bookmarks
  }
}

// --- Sharing helpers (module-level, stable references) ---
function buildShareText(text) {
  const truncated = text.length > 200 ? text.slice(0, 200) + "..." : text;
  return `"${truncated}"\n\n\u2014 The Human Dominion, by Artif Anony`;
}

function shareToTwitter(text) {
  const truncated = text.length > 200 ? text.slice(0, 200) + "..." : text;
  const tweetText = `"${truncated}"\n\n\u2014 The Human Dominion`;
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(LANDING_URL)}`;
  window.open(url, "_blank", "noopener,noreferrer,width=600,height=400");
}

function shareToFacebook(text) {
  const truncated = text.length > 200 ? text.slice(0, 200) + "..." : text;
  const quote = `"${truncated}" \u2014 The Human Dominion`;
  const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(LANDING_URL)}&quote=${encodeURIComponent(quote)}`;
  window.open(url, "_blank", "noopener,noreferrer,width=600,height=400");
}

const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

// ==========================================
// Extracted Components
// ==========================================

function ResumePrompt({ chapters, resumeData, onResume, onDismiss }) {
  if (!resumeData) return null;
  const chapter = chapters[resumeData.chapterIndex];
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="resume-prompt">
      <div className="border-2 border-terminal-green bg-[#0a0a0a] p-6 max-w-sm w-full space-y-4">
        <div className="font-mono text-xs uppercase tracking-widest text-terminal-green terminal-glow">Continue Reading?</div>
        <p className="text-[#d4d4d4] text-sm">
          You were on <span className="text-[#e5e5e5] font-semibold">Chapter {chapter?.chapter_number}: {chapter?.title}</span>
        </p>
        <div className="flex gap-3">
          <button onClick={onResume} className="flex-1 rounded-none border-2 border-terminal-green bg-transparent text-terminal-green hover:bg-terminal-green hover:text-obsidian brutal-btn font-mono uppercase text-xs py-2 font-bold" data-testid="resume-yes-btn">Resume</button>
          <button onClick={onDismiss} className="flex-1 rounded-none border-2 border-[#262626] bg-transparent text-[#a3a3a3] hover:border-[#404040] hover:text-[#e5e5e5] brutal-btn font-mono uppercase text-xs py-2" data-testid="resume-no-btn">Start Over</button>
        </div>
      </div>
    </div>
  );
}

function SearchModal({ open, query, results, onQueryChange, onSelect, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-black/80 backdrop-blur-sm" data-testid="search-modal">
      <div className="max-w-2xl w-full mx-auto mt-20 p-4">
        <div className="flex items-center gap-3 border-2 border-signal-red bg-[#0a0a0a] px-4 py-3">
          <Search className="w-5 h-5 text-signal-red shrink-0" />
          <input autoFocus type="text" value={query} onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search across all chapters..." className="flex-1 bg-transparent text-[#e5e5e5] font-mono text-sm placeholder:text-[#525252] outline-none" data-testid="search-input" />
          <button onClick={onClose} className="text-[#a3a3a3] hover:text-[#e5e5e5]"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-3 max-h-[60vh] overflow-y-auto space-y-2" data-testid="search-results">
          {query.length >= 2 && results.length === 0 && (
            <p className="text-[#525252] font-mono text-xs text-center py-6">No results found</p>
          )}
          {results.map((r) => (
            <button key={`${r.chapterIndex}-${r.matchStart}`} onClick={() => onSelect(r)}
              className="w-full text-left border border-[#262626] bg-[#0a0a0a] hover:border-signal-red/50 p-3 transition-colors"
              data-testid={`search-result-${r.chapterIndex}-${r.matchStart}`}>
              <div className="font-mono text-xs text-signal-red mb-1">Ch {r.chapterNumber}: {r.chapterTitle}</div>
              <p className="text-[#a3a3a3] text-xs leading-relaxed">
                ...{r.snippet.slice(0, r.matchStart)}
                <span className="text-[#e5e5e5] bg-signal-red/20 px-0.5">{r.snippet.slice(r.matchStart, r.matchStart + r.matchLen)}</span>
                {r.snippet.slice(r.matchStart + r.matchLen)}...
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BookmarkItem({ bm, copiedId, onGoto, onRemove, onCopy, onTweet, onFacebook, onNativeShare }) {
  return (
    <div className="border border-[#262626] bg-[#111] p-3 group">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => onGoto(bm)} className="font-mono text-xs text-signal-red hover:underline" data-testid={`bookmark-goto-${bm.id}`}>
          Ch {bm.chapterNumber}: {bm.chapterTitle}
        </button>
        <button onClick={() => onRemove(bm.id)} className="opacity-0 group-hover:opacity-100 text-[#525252] hover:text-signal-red transition-opacity" data-testid={`bookmark-remove-${bm.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-[#a3a3a3] text-xs leading-relaxed italic">"{bm.text}"</p>
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#1a1a1a]">
        <button onClick={() => onCopy(bm.text, bm.id)} className="flex items-center gap-1 text-[#525252] hover:text-terminal-green font-mono text-[10px] uppercase tracking-wider transition-colors" data-testid={`bookmark-copy-${bm.id}`}>
          {copiedId === bm.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copiedId === bm.id ? "Copied" : "Copy"}
        </button>
        <button onClick={() => onTweet(bm.text)} className="flex items-center gap-1 text-[#525252] hover:text-[#1DA1F2] font-mono text-[10px] uppercase tracking-wider transition-colors" data-testid={`bookmark-tweet-${bm.id}`}>
          <Twitter className="w-3 h-3" /> Tweet
        </button>
        <button onClick={() => onFacebook(bm.text)} className="flex items-center gap-1 text-[#525252] hover:text-[#1877F2] font-mono text-[10px] uppercase tracking-wider transition-colors" data-testid={`bookmark-fb-${bm.id}`}>
          <Facebook className="w-3 h-3" /> Share
        </button>
        {canNativeShare && (
          <button onClick={() => onNativeShare(bm.text)} className="flex items-center gap-1 text-[#525252] hover:text-[#e5e5e5] font-mono text-[10px] uppercase tracking-wider transition-colors" data-testid={`bookmark-share-${bm.id}`}>
            <Share2 className="w-3 h-3" /> Share
          </button>
        )}
        <span className="font-mono text-[10px] text-[#333] ml-auto">{new Date(bm.createdAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function BookmarksPanel({ open, bookmarks, copiedId, onClose, onGoto, onRemove, onCopy, onTweet, onFacebook, onNativeShare }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="bookmarks-panel">
      <div className="w-full max-w-md bg-[#0a0a0a] border-l-2 border-signal-red h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-xs uppercase tracking-widest text-signal-red">Bookmarks ({bookmarks.length})</div>
          <button onClick={onClose} className="text-[#a3a3a3] hover:text-[#e5e5e5]"><X className="w-5 h-5" /></button>
        </div>
        {bookmarks.length === 0 ? (
          <p className="text-[#525252] font-mono text-xs py-8 text-center">No bookmarks yet. Select text while reading and tap the bookmark button to save passages.</p>
        ) : (
          <div className="space-y-3">
            {bookmarks.map((bm) => (
              <BookmarkItem key={bm.id} bm={bm} copiedId={copiedId} onGoto={onGoto} onRemove={onRemove} onCopy={onCopy} onTweet={onTweet} onFacebook={onFacebook} onNativeShare={onNativeShare} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FloatingActions({ selectedText, copiedId, onBookmark, onCopy, onTweet, onFacebook }) {
  if (!selectedText) return null;
  return (
    <div className="fixed bottom-24 right-6 z-[70] animate-in fade-in slide-in-from-bottom-2 flex flex-col gap-2" data-testid="bookmark-fab">
      <button onClick={onBookmark} className="flex items-center gap-2 rounded-none border-2 border-terminal-green bg-[#0a0a0a] text-terminal-green hover:bg-terminal-green hover:text-obsidian brutal-btn font-mono uppercase text-xs px-4 py-3 font-bold shadow-lg shadow-terminal-green/20" data-testid="add-bookmark-btn">
        <Bookmark className="w-4 h-4" /> Bookmark
      </button>
      <div className="flex gap-2">
        <button onClick={() => onCopy(selectedText, "selection")} className="flex-1 flex items-center justify-center gap-1.5 rounded-none border border-[#404040] bg-[#0a0a0a] text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green font-mono uppercase text-[10px] px-3 py-2 font-bold shadow-lg" data-testid="fab-copy-btn">
          {copiedId === "selection" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copiedId === "selection" ? "Copied" : "Copy"}
        </button>
        <button onClick={() => onTweet(selectedText)} className="flex-1 flex items-center justify-center gap-1.5 rounded-none border border-[#404040] bg-[#0a0a0a] text-[#a3a3a3] hover:border-[#1DA1F2] hover:text-[#1DA1F2] font-mono uppercase text-[10px] px-3 py-2 font-bold shadow-lg" data-testid="fab-tweet-btn">
          <Twitter className="w-3 h-3" /> Tweet
        </button>
        <button onClick={() => onFacebook(selectedText)} className="flex-1 flex items-center justify-center gap-1.5 rounded-none border border-[#404040] bg-[#0a0a0a] text-[#a3a3a3] hover:border-[#1877F2] hover:text-[#1877F2] font-mono uppercase text-[10px] px-3 py-2 font-bold shadow-lg" data-testid="fab-facebook-btn">
          <Facebook className="w-3 h-3" /> FB
        </button>
      </div>
    </div>
  );
}

function ChapterSidebar({ chapters, currentIndex, currentChapter, onSelect, onPrev, onNext }) {
  return (
    <div className="space-y-4">
      <div className="rounded-none border-2 border-signal-red bg-[#0a0a0a] p-5" data-testid="chapter-info">
        <div className="font-mono text-xs uppercase tracking-widest text-[#525252] mb-2">Chapter {currentChapter?.chapter_number} of {chapters.length}</div>
        <h2 className="font-['Syne'] text-xl font-bold text-signal-red signal-glow mb-4">{currentChapter?.title}</h2>
        <div className="flex gap-2">
          <button onClick={onPrev} disabled={currentIndex === 0} className="flex-1 rounded-none border-2 border-[#262626] bg-transparent text-[#e5e5e5] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase text-xs py-2 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1" data-testid="previous-btn">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <button onClick={onNext} disabled={currentIndex === chapters.length - 1} className="flex-1 rounded-none border-2 border-[#262626] bg-transparent text-[#e5e5e5] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase text-xs py-2 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1" data-testid="next-btn">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="rounded-none border border-[#262626] bg-[#0a0a0a] p-4" data-testid="chapter-list">
        <div className="font-mono text-xs uppercase tracking-widest text-[#525252] mb-3">All Chapters</div>
        <div className="space-y-1">
          {chapters.map((chapter, index) => (
            <button key={chapter.id} onClick={() => onSelect(index)}
              className={`w-full text-left rounded-none border p-2 brutal-btn font-mono text-xs transition-all ${
                index === currentIndex ? "border-signal-red bg-signal-red/10 text-signal-red" : "border-[#262626] bg-transparent text-[#a3a3a3] hover:border-[#404040] hover:text-[#e5e5e5]"
              }`}
              data-testid={`chapter-${index + 1}-btn`}>
              <span className="opacity-60">Ch {chapter.chapter_number}:</span> {chapter.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Main Component
// ==========================================
export default function ReaderPage() {
  const navigate = useNavigate();
  const { bookNumber } = useParams();
  const contentRef = useRef(null);

  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState("base");
  const [readingMode, setReadingMode] = useState("split");

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [resumePrompt, setResumePrompt] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // ------ Data fetching ------
  const fetchBookData = useCallback(async () => {
    try {
      const [bookRes, chaptersRes] = await Promise.all([
        axios.get(`${API}/books/${bookNumber}`),
        axios.get(`${API}/books/${bookNumber}/chapters`),
      ]);
      setBook(bookRes.data);
      setChapters(chaptersRes.data);
      setLoading(false);
      const saved = loadProgress(bookNumber);
      if (saved && saved.chapterIndex > 0) {
        setResumePrompt(saved);
      }
    } catch (error) {
      // Error fetching book
      toast.error("Failed to load book");
      setLoading(false);
    }
  }, [bookNumber]);

  useEffect(() => { fetchBookData(); }, [fetchBookData]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [currentChapterIndex]);

  useEffect(() => {
    if (chapters.length > 0) saveProgress(bookNumber, currentChapterIndex);
  }, [currentChapterIndex, bookNumber, chapters.length]);

  useEffect(() => { setBookmarks(loadBookmarks(bookNumber)); }, [bookNumber]);

  useEffect(() => {
    const handleSelection = () => {
      const sel = window.getSelection();
      const text = sel?.toString().trim();
      setSelectedText(text && text.length > 2 && text.length < 500 ? text : "");
    };
    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("touchend", handleSelection);
    return () => {
      document.removeEventListener("mouseup", handleSelection);
      document.removeEventListener("touchend", handleSelection);
    };
  }, []);

  // ------ Navigation ------
  const handleNext = useCallback(() => {
    setCurrentChapterIndex(i => i < chapters.length - 1 ? i + 1 : i);
  }, [chapters.length]);

  const handlePrevious = useCallback(() => {
    setCurrentChapterIndex(i => i > 0 ? i - 1 : i);
  }, []);

  // ------ EPUB Download ------
  const handleDownloadEpub = useCallback(async () => {
    try {
      toast.loading("Generating EPUB...");
      const response = await axios.get(`${API}/books/${bookNumber}/epub`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `human_dominion_book_${bookNumber}.epub`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success("EPUB downloaded!");
    } catch (err) {
      // EPUB download failed
      toast.dismiss();
      toast.error("Failed to download EPUB");
    }
  }, [bookNumber]);

  // ------ Search ------
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query || query.length < 2) { setSearchResults([]); return; }
    const lower = query.toLowerCase();
    const results = [];
    chapters.forEach((ch, idx) => {
      const content = ch.content.toLowerCase();
      let startIdx = 0;
      while (startIdx < content.length && results.length < 50) {
        const pos = content.indexOf(lower, startIdx);
        if (pos === -1) break;
        const snippetStart = Math.max(0, pos - 60);
        const snippetEnd = Math.min(ch.content.length, pos + query.length + 60);
        results.push({
          chapterIndex: idx, chapterNumber: ch.chapter_number, chapterTitle: ch.title,
          snippet: ch.content.slice(snippetStart, snippetEnd),
          matchStart: pos - snippetStart, matchLen: query.length,
        });
        startIdx = pos + query.length;
      }
    });
    setSearchResults(results);
  }, [chapters]);

  const closeSearch = useCallback(() => { setSearchOpen(false); setSearchQuery(""); setSearchResults([]); }, []);
  const goToSearchResult = useCallback((result) => { setCurrentChapterIndex(result.chapterIndex); closeSearch(); }, [closeSearch]);

  // ------ Bookmarks ------
  const addBookmark = useCallback(() => {
    if (!selectedText || !chapters[currentChapterIndex]) return;
    const newBm = {
      id: Date.now().toString(),
      chapterIndex: currentChapterIndex,
      chapterNumber: chapters[currentChapterIndex].chapter_number,
      chapterTitle: chapters[currentChapterIndex].title,
      text: selectedText,
      createdAt: new Date().toISOString(),
    };
    const updated = [newBm, ...bookmarks];
    setBookmarks(updated);
    persistBookmarks(bookNumber, updated);
    setSelectedText("");
    window.getSelection()?.removeAllRanges();
    toast.success("Passage bookmarked");
  }, [selectedText, chapters, currentChapterIndex, bookmarks, bookNumber]);

  const removeBookmark = useCallback((id) => {
    const updated = bookmarks.filter((b) => b.id !== id);
    setBookmarks(updated);
    persistBookmarks(bookNumber, updated);
    toast.success("Bookmark removed");
  }, [bookmarks, bookNumber]);

  const goToBookmark = useCallback((bm) => { setCurrentChapterIndex(bm.chapterIndex); setBookmarksOpen(false); }, []);

  // ------ Sharing ------
  const handleCopyQuote = useCallback((text, id) => {
    const shareText = buildShareText(text) + `\n${LANDING_URL}`;
    navigator.clipboard.writeText(shareText).then(() => {
      setCopiedId(id || "selection");
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("Quote copied to clipboard");
    });
  }, []);

  const handleNativeShare = useCallback(async (text) => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: "The Human Dominion", text: buildShareText(text), url: LANDING_URL });
    } catch (e) {
      // Native share cancelled or failed
    }
  }, []);

  // ------ Resume ------
  const handleResume = useCallback(() => { if (resumePrompt) setCurrentChapterIndex(resumePrompt.chapterIndex); setResumePrompt(null); }, [resumePrompt]);
  const dismissResume = useCallback(() => setResumePrompt(null), []);

  // ------ Helpers ------
  const getFontSizeClass = () => {
    switch (fontSize) {
      case "small": return "text-sm md:text-base";
      case "base": return "text-base md:text-lg";
      case "large": return "text-lg md:text-xl";
      case "xlarge": return "text-xl md:text-2xl";
      default: return "text-base md:text-lg";
    }
  };

  const currentChapter = chapters[currentChapterIndex];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian">
        <Loader2 className="w-12 h-12 animate-spin text-terminal-green" />
      </div>
    );
  }

  if (!book || chapters.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian">
        <div className="text-center space-y-4">
          <p className="text-signal-red font-mono" data-testid="no-chapters-message">Book not found or no chapters available.</p>
          <button onClick={() => navigate("/")} className="rounded-none border-2 border-terminal-green bg-transparent text-terminal-green hover:bg-terminal-green hover:text-obsidian brutal-btn font-mono uppercase tracking-wide px-6 py-3 text-sm font-bold" data-testid="go-home-btn">Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grain-overlay" />
      <div className="scanlines" />
      <div className="vignette" />

      <ResumePrompt chapters={chapters} resumeData={resumePrompt} onResume={handleResume} onDismiss={dismissResume} />
      <SearchModal open={searchOpen} query={searchQuery} results={searchResults} onQueryChange={handleSearch} onSelect={goToSearchResult} onClose={closeSearch} />
      <BookmarksPanel open={bookmarksOpen} bookmarks={bookmarks} copiedId={copiedId} onClose={() => setBookmarksOpen(false)} onGoto={goToBookmark} onRemove={removeBookmark} onCopy={handleCopyQuote} onTweet={shareToTwitter} onFacebook={shareToFacebook} onNativeShare={handleNativeShare} />
      <FloatingActions selectedText={selectedText} copiedId={copiedId} onBookmark={addBookmark} onCopy={handleCopyQuote} onTweet={shareToTwitter} onFacebook={shareToFacebook} />

      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <div className="border-b border-[#262626] bg-[#0a0a0a]/90 backdrop-blur-sm sticky top-0 z-50 p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="rounded-none border-2 border-[#262626] bg-transparent text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase tracking-wide px-3 py-2 text-xs font-bold flex items-center gap-2" data-testid="back-home-btn">
                <Home className="w-4 h-4" /> Home
              </button>
              <div className="hidden md:block">
                <div className="font-['Syne'] text-sm font-bold text-[#e5e5e5]">{book.title}</div>
                <div className="font-mono text-xs text-[#525252]">Book {book.book_number} of 3</div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={() => setSearchOpen(true)} className="rounded-none border border-[#262626] bg-transparent text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono text-xs px-3 py-1 flex items-center gap-2" data-testid="search-btn">
                <Search className="w-4 h-4" /><span className="hidden sm:inline">Search</span>
              </button>
              <button onClick={() => setBookmarksOpen(true)} className={`rounded-none border bg-transparent brutal-btn font-mono text-xs px-3 py-1 flex items-center gap-2 ${bookmarks.length > 0 ? "border-signal-red/50 text-signal-red hover:border-signal-red" : "border-[#262626] text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green"}`} data-testid="bookmarks-btn">
                {bookmarks.length > 0 ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                <span className="hidden sm:inline">{bookmarks.length || ""}</span>
              </button>
              <div className="hidden sm:flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#525252]" />
                <select value={fontSize} onChange={(e) => setFontSize(e.target.value)} className="rounded-none border border-[#262626] bg-[#0a0a0a] text-[#e5e5e5] font-mono text-xs px-2 py-1 focus:outline-none focus:border-terminal-green" data-testid="font-size-selector">
                  <option value="small">Small</option><option value="base">Normal</option><option value="large">Large</option><option value="xlarge">X-Large</option>
                </select>
              </div>
              <button onClick={() => setReadingMode(readingMode === "split" ? "fulltext" : "split")} className="rounded-none border border-[#262626] bg-transparent text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono text-xs px-3 py-1 flex items-center gap-2" data-testid="reading-mode-toggle">
                <BookOpen className="w-4 h-4" /><span className="hidden sm:inline">{readingMode === "split" ? "Full" : "Split"}</span>
              </button>
              <button onClick={handleDownloadEpub} className="rounded-none border border-[#262626] bg-transparent text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono text-xs px-3 py-1 flex items-center gap-2" data-testid="download-epub-btn">
                <Download className="w-4 h-4" /><span className="hidden sm:inline">EPUB</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-8">
          <div className={`max-w-7xl mx-auto ${readingMode === "split" ? "grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6" : ""}`}>
            {readingMode === "split" && (
              <ChapterSidebar chapters={chapters} currentIndex={currentChapterIndex} currentChapter={currentChapter} onSelect={setCurrentChapterIndex} onPrev={handlePrevious} onNext={handleNext} />
            )}
            <div ref={contentRef} className="rounded-none border border-[#262626] bg-[#0a0a0a] p-6 md:p-10" data-testid="chapter-text">
              {readingMode === "fulltext" && (
                <div className="mb-8 pb-6 border-b border-[#262626]">
                  <div className="font-mono text-xs uppercase tracking-widest text-[#525252] mb-2">Chapter {currentChapter?.chapter_number} of {chapters.length}</div>
                  <h2 className="font-['Syne'] text-3xl font-bold text-signal-red signal-glow mb-4">{currentChapter?.title}</h2>
                  <div className="flex gap-2">
                    <button onClick={handlePrevious} disabled={currentChapterIndex === 0} className="rounded-none border-2 border-[#262626] bg-transparent text-[#e5e5e5] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase text-xs px-4 py-2 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1">
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    <button onClick={handleNext} disabled={currentChapterIndex === chapters.length - 1} className="rounded-none border-2 border-[#262626] bg-transparent text-[#e5e5e5] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase text-xs px-4 py-2 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1">
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              <div className="prose prose-invert max-w-none">
                <div className={`text-[#d4d4d4] leading-relaxed whitespace-pre-wrap ${getFontSizeClass()}`} style={{ lineHeight: "1.9" }} data-testid="chapter-content">
                  {currentChapter?.content}
                </div>
              </div>
              <div className="mt-10 pt-6 border-t border-[#262626] flex justify-between items-center">
                <button onClick={handlePrevious} disabled={currentChapterIndex === 0} className="rounded-none border-2 border-[#262626] bg-transparent text-[#e5e5e5] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase text-xs px-5 py-2 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2" data-testid="bottom-previous-btn">
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <div className="font-mono text-xs text-[#525252]">{currentChapterIndex + 1} / {chapters.length}</div>
                <button onClick={handleNext} disabled={currentChapterIndex === chapters.length - 1} className="rounded-none border-2 border-[#262626] bg-transparent text-[#e5e5e5] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase text-xs px-5 py-2 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2" data-testid="bottom-next-btn">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
